import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { salonAPI, staffAPI, servicesAPI, remindersAPI, clientsAPI, authAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { 
    Building2, 
    Users, 
    Scissors, 
    Bell, 
    Plus,
    Trash2,
    Save,
    Loader2,
    FileText,
    Mail,
    CreditCard,
    Key,
    Send,
    Download,
    AlertCircle,
    CheckCircle2,
    Edit,
    Search,
    MessageSquare,
    Smartphone,
    Clock,
    RefreshCw,
    TestTube,
    Sparkles,
    Settings2,
    ExternalLink,
    Zap
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, cn } from "@/lib/utils";
import { format } from "date-fns";

// Auto-refresh interval (3 minutes in milliseconds)
const AUTO_REFRESH_INTERVAL = 3 * 60 * 1000;

export default function SettingsPage() {
    const { user, updateUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [testingNotification, setTestingNotification] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [staff, setStaff] = useState([]);
    const [services, setServices] = useState([]);
    const [clients, setClients] = useState([]);
    const [logs, setLogs] = useState([]);
    const [logsFilter, setLogsFilter] = useState({ level: 'all', action: 'all' });
    
    // Form states
    const [salonForm, setSalonForm] = useState({
        business_name: user?.business_name || "",
        owner_name: user?.owner_name || "",
        phone: user?.phone || "",
        address: user?.address || "",
        description: user?.description || ""
    });

    const [paystackForm, setPaystackForm] = useState({
        paystack_secret_key: "",
        paystack_public_key: "",
        paystack_enabled: user?.paystack_enabled || false
    });

    const [bookingFeeForm, setBookingFeeForm] = useState({
        booking_fee_enabled: user?.booking_fee_enabled || false,
        booking_fee_amount: user?.booking_fee_amount || 10,
        booking_fee_percentage: user?.booking_fee_percentage || 10,
        use_percentage_fee: user?.use_percentage_fee || false
    });

    const [notificationSettings, setNotificationSettings] = useState({
        email_enabled: user?.email_enabled ?? true,
        sms_enabled: user?.sms_enabled ?? true,
        reminder_hours_before: user?.reminder_hours_before || 24,
        follow_up_enabled: user?.follow_up_enabled ?? true,
        follow_up_days: user?.follow_up_days || 14
    });

    // Dialog states
    const [showMessageDialog, setShowMessageDialog] = useState(false);
    const [showStaffDialog, setShowStaffDialog] = useState(false);
    const [showServiceDialog, setShowServiceDialog] = useState(false);
    const [showTestNotificationDialog, setShowTestNotificationDialog] = useState(false);
    
    const [messageForm, setMessageForm] = useState({
        recipient_type: 'all',
        client_id: '',
        subject: '',
        message: '',
        send_sms: true,
        send_email: true
    });

    const [testNotificationForm, setTestNotificationForm] = useState({
        phone: user?.phone || '',
        email: user?.email || '',
        type: 'both'
    });

    const [editingStaff, setEditingStaff] = useState(null);
    const [editingService, setEditingService] = useState(null);
    const [staffForm, setStaffForm] = useState({ name: "", phone: "", email: "", role: "staff" });
    const [serviceForm, setServiceForm] = useState({ 
        name: "", 
        description: "", 
        price: "", 
        duration_minutes: "30",
        category: ""
    });
    
    const [staffSearchTerm, setStaffSearchTerm] = useState("");
    const [serviceSearchTerm, setServiceSearchTerm] = useState("");

    // Refresh user data to get latest credits
    // Refresh user data to get latest credits
const refreshUserData = useCallback(async (showToast = false) => {
    try {
        setRefreshing(true);
        const res = await authAPI.getMe();
        
        // Handle nested response: { success: true, data: { user: {...} } }
        const userData = res.data?.data?.user || res.data?.user || res.data?.data || res.data;
        
        // Only update if we got valid user data (must have email or id)
        if (userData && (userData.email || userData.id)) {
            // Merge with existing user data to prevent losing data
            updateUser({ ...user, ...userData });
            setLastRefresh(new Date());
            if (showToast) {
                toast.success("Data refreshed!");
            }
        } else {
            console.warn("Refresh returned invalid user data:", res.data);
            setLastRefresh(new Date());
            if (showToast) {
                toast.error("Failed to refresh - invalid data received");
            }
        }
    } catch (error) {
        console.error("Failed to refresh user data", error);
        if (showToast) {
            toast.error("Failed to refresh data");
        }
    } finally {
        setRefreshing(false);
    }
}, [updateUser, user]);

    // Auto-refresh every 3 minutes
    useEffect(() => {
        const intervalId = setInterval(() => {
            refreshUserData(false);
        }, AUTO_REFRESH_INTERVAL);

        return () => clearInterval(intervalId);
    }, [refreshUserData]);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (user) {
            setSalonForm({
                business_name: user.business_name || "",
                owner_name: user.owner_name || "",
                phone: user.phone || "",
                address: user.address || "",
                description: user.description || ""
            });
            setBookingFeeForm({
                booking_fee_enabled: user.booking_fee_enabled || false,
                booking_fee_amount: user.booking_fee_amount || 10,
                booking_fee_percentage: user.booking_fee_percentage || 10,
                use_percentage_fee: user.use_percentage_fee || false
            });
            setPaystackForm(prev => ({
                ...prev,
                paystack_enabled: user.paystack_enabled || false
            }));
            setNotificationSettings({
                email_enabled: user.email_enabled ?? true,
                sms_enabled: user.sms_enabled ?? true,
                reminder_hours_before: user.reminder_hours_before || 24,
                follow_up_enabled: user.follow_up_enabled ?? true,
                follow_up_days: user.follow_up_days || 14
            });
            setTestNotificationForm(prev => ({
                ...prev,
                phone: user.phone || '',
                email: user.email || ''
            }));
        }
    }, [user]);

    const loadData = async () => {
        try {
            const [staffRes, servicesRes, clientsRes] = await Promise.all([
                staffAPI.getAll(),
                servicesAPI.getAll(),
                clientsAPI.getAll()
            ]);
            
            setStaff(extractDataFromResponse(staffRes.data, 'staff'));
            setServices(extractDataFromResponse(servicesRes.data, 'services'));
            setClients(extractDataFromResponse(clientsRes.data, 'clients'));
        } catch (error) {
            console.error("Failed to load settings data", error);
            toast.error("Failed to load settings data");
            setStaff([]);
            setServices([]);
            setClients([]);
        }
    };

    const extractDataFromResponse = (response, key) => {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        if (response.data && Array.isArray(response.data)) return response.data;
        if (response[key] && Array.isArray(response[key])) return response[key];
        if (response.success && Array.isArray(response.data)) return response.data;
        return [];
    };

    const loadLogs = async () => {
        try {
            const params = new URLSearchParams();
            if (logsFilter.level !== 'all') params.append('level', logsFilter.level);
            if (logsFilter.action !== 'all') params.append('action', logsFilter.action);
            params.append('limit', '100');
            
            const res = await salonAPI.getLogs(params.toString());
            setLogs(extractDataFromResponse(res.data, 'logs'));
        } catch (error) {
            console.error("Failed to load logs", error);
            setLogs([]);
        }
    };

    useEffect(() => {
        loadLogs();
    }, [logsFilter]);

    // Handlers
    const handleSaveSalon = async () => {
        setLoading(true);
        try {
            const res = await salonAPI.updateProfile(salonForm);
            updateUser({ ...user, ...res.data?.data || res.data });
            toast.success("Business profile updated!");
        } catch (error) {
            toast.error("Failed to update profile");
        } finally {
            setLoading(false);
        }
    };

    const handleSavePaystack = async () => {
        if (paystackForm.paystack_enabled && (!paystackForm.paystack_secret_key || !paystackForm.paystack_public_key)) {
            toast.error("Please provide both secret and public keys");
            return;
        }
        setLoading(true);
        try {
            const res = await salonAPI.updatePaystackSettings(paystackForm);
            updateUser({ ...user, ...res.data?.data || res.data });
            toast.success("Paystack settings saved!");
        } catch (error) {
            toast.error("Failed to save Paystack settings");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveBookingFee = async () => {
        setLoading(true);
        try {
            const res = await salonAPI.updateBookingSettings(bookingFeeForm);
            updateUser({ ...user, ...res.data?.data || res.data });
            toast.success("Booking fee settings saved!");
        } catch (error) {
            toast.error("Failed to save booking fee settings");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveNotifications = async () => {
        setLoading(true);
        try {
            const res = await remindersAPI.updateSettings(notificationSettings);
            updateUser({ ...user, ...notificationSettings });
            toast.success("Notification settings saved!");
        } catch (error) {
            toast.error("Failed to save notification settings");
        } finally {
            setLoading(false);
        }
    };

    const handleTestNotification = async () => {
        if (!testNotificationForm.phone && !testNotificationForm.email) {
            toast.error("Please provide a phone number or email");
            return;
        }
        
        setTestingNotification(true);
        try {
            await remindersAPI.sendTestNotification(testNotificationForm);
            toast.success("Test notification sent! (1 credit used)");
            setShowTestNotificationDialog(false);
            // Refresh user data to get updated credits
            await refreshUserData(false);
        } catch (error) {
            toast.error("Failed to send test notification");
        } finally {
            setTestingNotification(false);
        }
    };

    const handleSendMessage = async () => {
        if (!messageForm.subject || !messageForm.message) {
            toast.error("Subject and message are required");
            return;
        }
        if (!messageForm.send_sms && !messageForm.send_email) {
            toast.error("Please select at least one delivery method");
            return;
        }
        
        setLoading(true);
        try {
            await remindersAPI.sendMessage(messageForm);
            toast.success("Message sent successfully!");
            setShowMessageDialog(false);
            setMessageForm({
                recipient_type: 'all',
                client_id: '',
                subject: '',
                message: '',
                send_sms: true,
                send_email: true
            });
            // Refresh user data to get updated credits
            await refreshUserData(false);
        } catch (error) {
            toast.error("Failed to send message");
        } finally {
            setLoading(false);
        }
    };

    const handleExportLogs = async () => {
        try {
            const res = await salonAPI.exportLogs('csv');
            const blob = new Blob([res.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
            a.click();
            toast.success("Logs exported!");
        } catch (error) {
            if (logs.length > 0) {
                const headers = ['Level', 'Action', 'Message', 'Date'];
                const csvContent = [
                    headers.join(','),
                    ...logs.map(log => [
                        log.level,
                        log.action,
                        `"${log.message?.replace(/"/g, '""') || ''}"`,
                        format(new Date(log.created_at || new Date()), 'yyyy-MM-dd HH:mm:ss')
                    ].join(','))
                ].join('\n');
                
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
                a.click();
                toast.success("Logs exported!");
            } else {
                toast.error("No logs to export");
            }
        }
    };

    // Staff handlers
    const handleEditStaff = (staffMember) => {
        setEditingStaff(staffMember);
        setStaffForm({
            name: staffMember.name || "",
            phone: staffMember.phone || "",
            email: staffMember.email || "",
            role: staffMember.role || "staff"
        });
        setShowStaffDialog(true);
    };

    const handleSaveStaff = async () => {
        if (!staffForm.name || !staffForm.phone) {
            toast.error("Name and phone are required");
            return;
        }
        try {
            if (editingStaff) {
                await staffAPI.update(editingStaff.id, staffForm);
                toast.success("Staff updated!");
            } else {
                await staffAPI.create(staffForm);
                toast.success("Staff member added!");
            }
            setShowStaffDialog(false);
            setEditingStaff(null);
            setStaffForm({ name: "", phone: "", email: "", role: "staff" });
            loadData();
        } catch (error) {
            toast.error(editingStaff ? "Failed to update staff" : "Failed to add staff");
        }
    };

    const handleDeleteStaff = async (id) => {
        if (!window.confirm("Are you sure you want to remove this staff member?")) return;
        try {
            await staffAPI.delete(id);
            toast.success("Staff member removed");
            loadData();
        } catch (error) {
            toast.error("Failed to remove staff");
        }
    };

    // Service handlers
    const handleEditService = (service) => {
        setEditingService(service);
        setServiceForm({
            name: service.name || "",
            description: service.description || "",
            price: service.price?.toString() || "",
            duration_minutes: service.duration_minutes?.toString() || "30",
            category: service.category || ""
        });
        setShowServiceDialog(true);
    };

    const handleSaveService = async () => {
        if (!serviceForm.name || !serviceForm.price) {
            toast.error("Name and price are required");
            return;
        }
        try {
            const payload = {
                ...serviceForm,
                price: parseFloat(serviceForm.price) || 0,
                duration_minutes: parseInt(serviceForm.duration_minutes) || 30
            };
            
            if (editingService) {
                await servicesAPI.update(editingService.id, payload);
                toast.success("Service updated!");
            } else {
                await servicesAPI.create(payload);
                toast.success("Service added!");
            }
            setShowServiceDialog(false);
            setEditingService(null);
            setServiceForm({ name: "", description: "", price: "", duration_minutes: "30", category: "" });
            loadData();
        } catch (error) {
            toast.error(editingService ? "Failed to update service" : "Failed to add service");
        }
    };

    const handleDeleteService = async (id) => {
        if (!window.confirm("Are you sure you want to delete this service?")) return;
        try {
            await servicesAPI.delete(id);
            toast.success("Service deleted");
            loadData();
        } catch (error) {
            toast.error("Failed to delete service");
        }
    };

    const getLogLevelColor = (level) => {
        const colors = {
            info: 'bg-blue-100 text-blue-800',
            warning: 'bg-yellow-100 text-yellow-800',
            error: 'bg-red-100 text-red-800',
            critical: 'bg-red-200 text-red-900',
            debug: 'bg-gray-100 text-gray-800',
        };
        return colors[level] || 'bg-gray-100 text-gray-800';
    };

    // Filtering
    const filteredStaff = (Array.isArray(staff) ? staff : []).filter(s => {
        if (!s) return false;
        const search = staffSearchTerm.toLowerCase();
        return (
            (s.name || '').toLowerCase().includes(search) ||
            (s.phone || '').includes(staffSearchTerm) ||
            (s.email || '').toLowerCase().includes(search)
        );
    });

    const filteredServices = (Array.isArray(services) ? services : []).filter(s => {
        if (!s) return false;
        const search = serviceSearchTerm.toLowerCase();
        return (
            (s.name || '').toLowerCase().includes(search) ||
            (s.description || '').toLowerCase().includes(search) ||
            (s.category || '').toLowerCase().includes(search)
        );
    });

    // Get notification credits (unified)
    const notificationCredits = user?.email_credits || 0;
    const creditsLow = notificationCredits <= 10;
    const creditsCritical = notificationCredits <= 5;

    return (
        <div className="p-4 md:p-8 lg:p-12 space-y-6" data-testid="settings-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Settings</h1>
                    <p className="text-muted-foreground">Manage your business settings and preferences</p>
                </div>
                
                {/* Quick Stats */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Unified Notification Credits */}
                    <div className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg border",
                        creditsCritical 
                            ? "bg-red-50 border-red-200 text-red-700" 
                            : creditsLow 
                                ? "bg-amber-50 border-amber-200 text-amber-700"
                                : "bg-primary/10 border-primary/20"
                    )}>
                        <Zap className={cn(
                            "w-4 h-4",
                            creditsCritical ? "text-red-500" : creditsLow ? "text-amber-500" : "text-primary"
                        )} />
                        <span className="text-sm font-medium">{notificationCredits} Notification Credits</span>
                    </div>
                    
                    <Badge variant={user?.subscription_status === 'active' ? 'default' : 'secondary'} className="px-3 py-2">
                        <Sparkles className="w-3 h-3 mr-1" />
                        {user?.subscription_status === 'active' ? 'Active' : user?.subscription_status || 'Trial'}
                    </Badge>
                    
                    {/* Refresh Button */}
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => refreshUserData(true)}
                        disabled={refreshing}
                        className="gap-1"
                    >
                        <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
                        <span className="hidden sm:inline text-xs text-muted-foreground">
                            {format(lastRefresh, 'HH:mm')}
                        </span>
                    </Button>
                </div>
            </div>

            {/* Low Credits Warning */}
            {creditsLow && (
                <Alert variant={creditsCritical ? "destructive" : "warning"} className={cn(
                    creditsCritical ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"
                )}>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="flex items-center justify-between">
                        <span>
                            {creditsCritical 
                                ? `Critical: Only ${notificationCredits} notification credits remaining!`
                                : `Low credits: ${notificationCredits} notification credits remaining.`
                            }
                        </span>
                        <Button size="sm" variant="outline" className="ml-4" asChild>
                            <a href="/subscription">Buy More Credits</a>
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            <Tabs defaultValue="profile" className="space-y-6">
                <TabsList className="flex flex-wrap h-auto gap-1 p-1 bg-muted/50">
                    <TabsTrigger value="profile" className="gap-2 data-[state=active]:bg-background">
                        <Building2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Profile</span>
                    </TabsTrigger>
                    <TabsTrigger value="staff" className="gap-2 data-[state=active]:bg-background">
                        <Users className="w-4 h-4" />
                        <span className="hidden sm:inline">Staff</span>
                    </TabsTrigger>
                    <TabsTrigger value="services" className="gap-2 data-[state=active]:bg-background">
                        <Scissors className="w-4 h-4" />
                        <span className="hidden sm:inline">Services</span>
                    </TabsTrigger>
                    <TabsTrigger value="notifications" className="gap-2 data-[state=active]:bg-background">
                        <Bell className="w-4 h-4" />
                        <span className="hidden sm:inline">Notifications</span>
                    </TabsTrigger>
                    <TabsTrigger value="payments" className="gap-2 data-[state=active]:bg-background">
                        <CreditCard className="w-4 h-4" />
                        <span className="hidden sm:inline">Payments</span>
                    </TabsTrigger>
                    <TabsTrigger value="messaging" className="gap-2 data-[state=active]:bg-background">
                        <MessageSquare className="w-4 h-4" />
                        <span className="hidden sm:inline">Messaging</span>
                    </TabsTrigger>
                    <TabsTrigger value="logs" className="gap-2 data-[state=active]:bg-background">
                        <FileText className="w-4 h-4" />
                        <span className="hidden sm:inline">Logs</span>
                    </TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Building2 className="w-5 h-5" />
                                Business Profile
                            </CardTitle>
                            <CardDescription>Update your business information visible to clients</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Business Name</Label>
                                    <Input
                                        value={salonForm.business_name}
                                        onChange={(e) => setSalonForm({ ...salonForm, business_name: e.target.value })}
                                        placeholder="Your business name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Owner Name</Label>
                                    <Input
                                        value={salonForm.owner_name}
                                        onChange={(e) => setSalonForm({ ...salonForm, owner_name: e.target.value })}
                                        placeholder="Your name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Phone</Label>
                                    <Input
                                        value={salonForm.phone}
                                        onChange={(e) => setSalonForm({ ...salonForm, phone: e.target.value })}
                                        placeholder="+233 XX XXX XXXX"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Address</Label>
                                    <Input
                                        value={salonForm.address}
                                        onChange={(e) => setSalonForm({ ...salonForm, address: e.target.value })}
                                        placeholder="Your business address"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Textarea
                                    value={salonForm.description}
                                    onChange={(e) => setSalonForm({ ...salonForm, description: e.target.value })}
                                    placeholder="Tell clients about your business, specialties, and what makes you unique..."
                                    rows={4}
                                />
                            </div>
                            
                            <div className="flex justify-end">
                                <Button onClick={handleSaveSalon} disabled={loading}>
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                    Save Changes
                                </Button>
                            </div>

                            <Separator />

                            {/* Subscription Info */}
                            <div className="p-4 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h3 className="font-semibold flex items-center gap-2">
                                            <Sparkles className="w-4 h-4 text-primary" />
                                            Subscription Status
                                        </h3>
                                        <p className="text-sm text-muted-foreground">Your current plan and usage</p>
                                    </div>
                                    <Badge variant={user?.subscription_status === 'active' ? 'default' : 'secondary'}>
                                        {user?.subscription_status === 'active' ? '✓ Active' : user?.subscription_status || 'Trial'}
                                    </Badge>
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className={cn(
                                        "text-center p-3 rounded-lg",
                                        creditsCritical ? "bg-red-50" : creditsLow ? "bg-amber-50" : "bg-background"
                                    )}>
                                        <p className={cn(
                                            "text-2xl font-bold",
                                            creditsCritical ? "text-red-600" : creditsLow ? "text-amber-600" : "text-primary"
                                        )}>
                                            {notificationCredits}
                                        </p>
                                        <p className="text-xs text-muted-foreground">Notification Credits</p>
                                    </div>
                                    <div className="text-center p-3 bg-background rounded-lg">
                                        <p className="text-2xl font-bold">{staff.length}</p>
                                        <p className="text-xs text-muted-foreground">Staff Members</p>
                                    </div>
                                    <div className="text-center p-3 bg-background rounded-lg">
                                        <p className="text-2xl font-bold">{services.length}</p>
                                        <p className="text-xs text-muted-foreground">Services</p>
                                    </div>
                                    <div className="text-center p-3 bg-background rounded-lg">
                                        <p className="text-2xl font-bold">{clients.length}</p>
                                        <p className="text-xs text-muted-foreground">Clients</p>
                                    </div>
                                </div>
                                
                                {user?.subscription_end && (
                                    <p className="text-sm text-muted-foreground mt-4">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {user?.subscription_status === 'active' ? 'Renews' : 'Expires'}: {format(new Date(user.subscription_end), 'PPP')}
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Staff Tab */}
                <TabsContent value="staff">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2">
                                    <Users className="w-5 h-5" />
                                    Staff Members
                                </CardTitle>
                                <CardDescription>{staff.length} team members</CardDescription>
                            </div>
                            <Button 
                                onClick={() => {
                                    setEditingStaff(null);
                                    setStaffForm({ name: "", phone: "", email: "", role: "staff" });
                                    setShowStaffDialog(true);
                                }}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Staff
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="relative mb-6 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search staff by name, phone, or email..."
                                    value={staffSearchTerm}
                                    onChange={(e) => setStaffSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>

                            {filteredStaff.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                    <p className="font-medium">{staffSearchTerm ? "No staff found" : "No staff members yet"}</p>
                                    <p className="text-sm">Add your first team member to get started</p>
                                </div>
                            ) : (
                                <div className="border rounded-lg overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Phone</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Role</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filteredStaff.map((s) => (
                                                <TableRow key={s.id}>
                                                    <TableCell className="font-medium">{s.name}</TableCell>
                                                    <TableCell>{s.phone}</TableCell>
                                                    <TableCell className="text-muted-foreground">{s.email || '-'}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="capitalize">{s.role}</Badge>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <Button size="sm" variant="ghost" onClick={() => handleEditStaff(s)}>
                                                                <Edit className="w-4 h-4" />
                                                            </Button>
                                                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteStaff(s.id)}>
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Services Tab */}
                <TabsContent value="services">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2">
                                    <Scissors className="w-5 h-5" />
                                    Services
                                </CardTitle>
                                <CardDescription>{services.length} services offered</CardDescription>
                            </div>
                            <Button 
                                onClick={() => {
                                    setEditingService(null);
                                    setServiceForm({ name: "", description: "", price: "", duration_minutes: "30", category: "" });
                                    setShowServiceDialog(true);
                                }}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Service
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="relative mb-6 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search services..."
                                    value={serviceSearchTerm}
                                    onChange={(e) => setServiceSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>

                            {filteredServices.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Scissors className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                    <p className="font-medium">{serviceSearchTerm ? "No services found" : "No services yet"}</p>
                                    <p className="text-sm">Add your first service to start accepting bookings</p>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredServices.map((service) => (
                                        <div key={service.id} className="p-4 rounded-xl border bg-card hover:shadow-md transition-shadow">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-medium truncate">{service.name}</h4>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        {service.duration_minutes} mins
                                                    </p>
                                                </div>
                                                <p className="text-lg font-bold text-primary shrink-0">
                                                    {formatCurrency(service.price)}
                                                </p>
                                            </div>
                                            {service.description && (
                                                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                                    {service.description}
                                                </p>
                                            )}
                                            <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t">
                                                <Button size="sm" variant="ghost" onClick={() => handleEditService(service)}>
                                                    <Edit className="w-4 h-4 mr-1" />
                                                    Edit
                                                </Button>
                                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteService(service.id)}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Notifications Tab */}
                <TabsContent value="notifications" className="space-y-6">
                    {/* Unified Credits Display */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Zap className="w-5 h-5" />
                                Notification Credits
                            </CardTitle>
                            <CardDescription>
                                1 credit = 1 notification (SMS + Email combined)
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className={cn(
                                "p-6 rounded-xl border-2 text-center",
                                creditsCritical 
                                    ? "bg-red-50 border-red-200" 
                                    : creditsLow 
                                        ? "bg-amber-50 border-amber-200"
                                        : "bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20"
                            )}>
                                <p className={cn(
                                    "text-5xl font-bold mb-2",
                                    creditsCritical ? "text-red-600" : creditsLow ? "text-amber-600" : "text-primary"
                                )}>
                                    {notificationCredits}
                                </p>
                                <p className="text-sm text-muted-foreground mb-4">credits remaining</p>
                                
                                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <Mail className="w-4 h-4" />
                                        <span>Email</span>
                                    </div>
                                    <span>+</span>
                                    <div className="flex items-center gap-1">
                                        <Smartphone className="w-4 h-4" />
                                        <span>SMS</span>
                                    </div>
                                    <span>=</span>
                                    <div className="flex items-center gap-1 font-medium text-foreground">
                                        <Zap className="w-4 h-4" />
                                        <span>1 Credit</span>
                                    </div>
                                </div>
                                
                                {creditsLow && (
                                    <Button className="mt-4" asChild>
                                        <a href="/subscription">
                                            <Plus className="w-4 h-4 mr-2" />
                                            Buy More Credits
                                        </a>
                                    </Button>
                                )}
                            </div>
                            
                            <p className="text-xs text-muted-foreground text-center mt-3">
                                Auto-refreshes every 3 minutes • Last updated: {format(lastRefresh, 'HH:mm:ss')}
                            </p>
                        </CardContent>
                    </Card>

                    {/* Notification Channels */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Settings2 className="w-5 h-5" />
                                Notification Channels
                            </CardTitle>
                            <CardDescription>Choose how to send notifications to your clients</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                {/* Email Notifications */}
                                <div className={cn(
                                    "p-4 rounded-xl border-2 transition-all",
                                    notificationSettings.email_enabled 
                                        ? "border-primary/50 bg-primary/5" 
                                        : "border-muted bg-muted/30"
                                )}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "w-10 h-10 rounded-full flex items-center justify-center",
                                                notificationSettings.email_enabled ? "bg-primary text-white" : "bg-muted"
                                            )}>
                                                <Mail className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-medium">Email Notifications</p>
                                                <p className="text-xs text-muted-foreground">Send emails to clients</p>
                                            </div>
                                        </div>
                                        <Switch 
                                            checked={notificationSettings.email_enabled}
                                            onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, email_enabled: v })}
                                        />
                                    </div>
                                </div>

                                {/* SMS Notifications */}
                                <div className={cn(
                                    "p-4 rounded-xl border-2 transition-all",
                                    notificationSettings.sms_enabled 
                                        ? "border-green-500/50 bg-green-500/5" 
                                        : "border-muted bg-muted/30"
                                )}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "w-10 h-10 rounded-full flex items-center justify-center",
                                                notificationSettings.sms_enabled ? "bg-green-500 text-white" : "bg-muted"
                                            )}>
                                                <Smartphone className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-medium">SMS Notifications</p>
                                                <p className="text-xs text-muted-foreground">Send SMS via Hubtel</p>
                                            </div>
                                        </div>
                                        <Switch 
                                            checked={notificationSettings.sms_enabled}
                                            onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, sms_enabled: v })}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <Alert>
                                <Zap className="h-4 w-4" />
                                <AlertDescription>
                                    <strong>Unified Credits:</strong> Each notification uses 1 credit, whether you send SMS only, email only, or both together.
                                </AlertDescription>
                            </Alert>
                        </CardContent>
                    </Card>

                    {/* Reminder Settings */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Bell className="w-5 h-5" />
                                Appointment Reminders
                            </CardTitle>
                            <CardDescription>Configure automatic reminders for upcoming appointments</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-2">
                                <Label>Send reminder before appointment</Label>
                                <Select 
                                    value={notificationSettings.reminder_hours_before.toString()}
                                    onValueChange={(v) => setNotificationSettings({ ...notificationSettings, reminder_hours_before: parseInt(v) })}
                                >
                                    <SelectTrigger className="max-w-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">1 hour before</SelectItem>
                                        <SelectItem value="2">2 hours before</SelectItem>
                                        <SelectItem value="6">6 hours before</SelectItem>
                                        <SelectItem value="12">12 hours before</SelectItem>
                                        <SelectItem value="24">24 hours before</SelectItem>
                                        <SelectItem value="48">48 hours before</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Clients will receive both SMS and email reminders (if enabled) • Uses 1 credit per reminder
                                </p>
                            </div>

                            <Separator />

                            {/* Follow-up Settings */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">Follow-up Messages</p>
                                        <p className="text-sm text-muted-foreground">
                                            Send thank-you messages after visits to encourage rebooking
                                        </p>
                                    </div>
                                    <Switch 
                                        checked={notificationSettings.follow_up_enabled}
                                        onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, follow_up_enabled: v })}
                                    />
                                </div>

                                {notificationSettings.follow_up_enabled && (
                                    <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                                        <Label>Send follow-up after</Label>
                                        <Select 
                                            value={notificationSettings.follow_up_days.toString()}
                                            onValueChange={(v) => setNotificationSettings({ ...notificationSettings, follow_up_days: parseInt(v) })}
                                        >
                                            <SelectTrigger className="max-w-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="7">7 days after visit</SelectItem>
                                                <SelectItem value="14">14 days after visit</SelectItem>
                                                <SelectItem value="21">21 days after visit</SelectItem>
                                                <SelectItem value="30">30 days after visit</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>

                            <Separator />

                            <div className="flex items-center justify-between">
                                <Button 
                                    variant="outline" 
                                    onClick={() => setShowTestNotificationDialog(true)}
                                >
                                    <TestTube className="w-4 h-4 mr-2" />
                                    Send Test Notification (1 credit)
                                </Button>
                                
                                <Button onClick={handleSaveNotifications} disabled={loading}>
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                    Save Settings
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* What Gets Sent */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">What notifications are sent?</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid md:grid-cols-2 gap-4 text-sm">
                                <div className="space-y-3">
                                    <h4 className="font-medium text-muted-foreground">To Clients (1 credit each)</h4>
                                    <ul className="space-y-2">
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                            Booking confirmation
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                            Appointment reminders
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                            Cancellation notices
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                            Payment confirmations
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                            Follow-up thank you
                                        </li>
                                    </ul>
                                </div>
                                <div className="space-y-3">
                                    <h4 className="font-medium text-muted-foreground">To You (Free - No credits used)</h4>
                                    <ul className="space-y-2">
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-blue-500" />
                                            New booking alerts
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-blue-500" />
                                            Cancellation alerts
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-blue-500" />
                                            Appointment completed
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-blue-500" />
                                            Payment received
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Payments Tab */}
                <TabsContent value="payments" className="space-y-6">
                    {/* Paystack Integration */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-start justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Key className="w-5 h-5" />
                                        Paystack Integration
                                    </CardTitle>
                                    <CardDescription>
                                        Accept payments from clients via mobile money, cards, and bank transfer
                                    </CardDescription>
                                </div>
                                {user?.paystack_enabled && (
                                    <Badge className="bg-green-100 text-green-800">
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        Connected
                                    </Badge>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    Get your API keys from{" "}
                                    <a 
                                        href="https://dashboard.paystack.com/#/settings/developers" 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="text-primary underline inline-flex items-center gap-1"
                                    >
                                        Paystack Dashboard
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </AlertDescription>
                            </Alert>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Secret Key</Label>
                                    <Input
                                        type="password"
                                        value={paystackForm.paystack_secret_key}
                                        onChange={(e) => setPaystackForm({ ...paystackForm, paystack_secret_key: e.target.value })}
                                        placeholder="sk_live_xxxxx or sk_test_xxxxx"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Public Key</Label>
                                    <Input
                                        value={paystackForm.paystack_public_key}
                                        onChange={(e) => setPaystackForm({ ...paystackForm, paystack_public_key: e.target.value })}
                                        placeholder="pk_live_xxxxx or pk_test_xxxxx"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/50">
                                <div>
                                    <p className="font-medium">Enable Paystack Payments</p>
                                    <p className="text-sm text-muted-foreground">Accept booking fees and payments</p>
                                </div>
                                <Switch 
                                    checked={paystackForm.paystack_enabled}
                                    onCheckedChange={(v) => setPaystackForm({ ...paystackForm, paystack_enabled: v })}
                                />
                            </div>

                            <div className="flex justify-end">
                                <Button onClick={handleSavePaystack} disabled={loading}>
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                    Save Paystack Settings
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Booking Fee Settings */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5" />
                                Booking Fee Settings
                            </CardTitle>
                            <CardDescription>
                                Require a deposit to confirm online bookings and reduce no-shows
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/50">
                                <div>
                                    <p className="font-medium">Enable Booking Fee</p>
                                    <p className="text-sm text-muted-foreground">Clients pay to confirm their appointment</p>
                                </div>
                                <Switch 
                                    checked={bookingFeeForm.booking_fee_enabled}
                                    onCheckedChange={(v) => setBookingFeeForm({ ...bookingFeeForm, booking_fee_enabled: v })}
                                />
                            </div>

                            {bookingFeeForm.booking_fee_enabled && (
                                <div className="space-y-4 p-4 rounded-xl border border-dashed">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium">Fee Type</p>
                                            <p className="text-sm text-muted-foreground">
                                                {bookingFeeForm.use_percentage_fee 
                                                    ? "Percentage of service price" 
                                                    : "Fixed amount"}
                                            </p>
                                        </div>
                                        <Switch 
                                            checked={bookingFeeForm.use_percentage_fee}
                                            onCheckedChange={(v) => setBookingFeeForm({ ...bookingFeeForm, use_percentage_fee: v })}
                                        />
                                    </div>

                                    {bookingFeeForm.use_percentage_fee ? (
                                        <div className="space-y-2">
                                            <Label>Percentage (%)</Label>
                                            <div className="flex items-center gap-2 max-w-xs">
                                                <Input
                                                    type="number"
                                                    value={bookingFeeForm.booking_fee_percentage}
                                                    onChange={(e) => setBookingFeeForm({ ...bookingFeeForm, booking_fee_percentage: parseFloat(e.target.value) || 0 })}
                                                    min="1"
                                                    max="100"
                                                />
                                                <span className="text-muted-foreground">%</span>
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Example: For a GH₵100 service, fee = GH₵{(100 * (bookingFeeForm.booking_fee_percentage / 100)).toFixed(2)}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <Label>Fixed Amount (GH₵)</Label>
                                            <Input
                                                type="number"
                                                value={bookingFeeForm.booking_fee_amount}
                                                onChange={(e) => setBookingFeeForm({ ...bookingFeeForm, booking_fee_amount: parseFloat(e.target.value) || 0 })}
                                                className="max-w-xs"
                                                min="1"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex justify-end">
                                <Button onClick={handleSaveBookingFee} disabled={loading}>
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                    Save Booking Fee Settings
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Messaging Tab */}
                <TabsContent value="messaging">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <MessageSquare className="w-5 h-5" />
                                Send Messages to Clients
                            </CardTitle>
                            <CardDescription>Send promotional or important messages to your clients</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Unified Credits Display */}
                            <div className={cn(
                                "p-4 rounded-xl border-2 text-center",
                                creditsCritical 
                                    ? "bg-red-50 border-red-200" 
                                    : creditsLow 
                                        ? "bg-amber-50 border-amber-200"
                                        : "bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20"
                            )}>
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Zap className={cn(
                                        "w-5 h-5",
                                        creditsCritical ? "text-red-500" : creditsLow ? "text-amber-500" : "text-primary"
                                    )} />
                                    <span className="font-medium">Notification Credits</span>
                                </div>
                                <p className={cn(
                                    "text-4xl font-bold",
                                    creditsCritical ? "text-red-600" : creditsLow ? "text-amber-600" : "text-primary"
                                )}>
                                    {notificationCredits}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    1 credit = 1 message (SMS + Email)
                                </p>
                            </div>

                            <Button onClick={() => setShowMessageDialog(true)} className="w-full" size="lg">
                                <Send className="w-4 h-4 mr-2" />
                                Compose New Message
                            </Button>

                            <Alert>
                                <Zap className="h-4 w-4" />
                                <AlertDescription>
                                    Each message to a client uses <strong>1 credit</strong>, whether sent via email, SMS, or both.
                                    Sending to {clients.length} clients would use {clients.length} credits.
                                </AlertDescription>
                            </Alert>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Logs Tab */}
                <TabsContent value="logs">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="w-5 h-5" />
                                    Activity Logs
                                </CardTitle>
                                <CardDescription>Track all activity in your business</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={loadLogs}>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Refresh
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleExportLogs} disabled={logs.length === 0}>
                                    <Download className="w-4 h-4 mr-2" />
                                    Export
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex flex-wrap gap-4">
                                <div className="space-y-1">
                                    <Label className="text-xs">Level</Label>
                                    <Select value={logsFilter.level} onValueChange={(v) => setLogsFilter({ ...logsFilter, level: v })}>
                                        <SelectTrigger className="w-[140px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Levels</SelectItem>
                                            <SelectItem value="info">Info</SelectItem>
                                            <SelectItem value="warning">Warning</SelectItem>
                                            <SelectItem value="error">Error</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">Action</Label>
                                    <Select value={logsFilter.action} onValueChange={(v) => setLogsFilter({ ...logsFilter, action: v })}>
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Actions</SelectItem>
                                            <SelectItem value="user_login">Login</SelectItem>
                                            <SelectItem value="appointment_created">Appointment Created</SelectItem>
                                            <SelectItem value="appointment_completed">Appointment Completed</SelectItem>
                                            <SelectItem value="credit_used">Credit Used</SelectItem>
                                            <SelectItem value="reminder_email_sent">Reminder Email</SelectItem>
                                            <SelectItem value="reminder_sms_sent">Reminder SMS</SelectItem>
                                            <SelectItem value="payment_success">Payment Success</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[80px]">Level</TableHead>
                                            <TableHead className="w-[150px]">Action</TableHead>
                                            <TableHead>Message</TableHead>
                                            <TableHead className="w-[160px]">Date</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {logs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                                                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                                    No logs found
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            logs.slice(0, 50).map((log) => (
                                                <TableRow key={log.id}>
                                                    <TableCell>
                                                        <Badge className={cn("text-xs", getLogLevelColor(log.level))}>
                                                            {log.level}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="font-mono text-xs">{log.action}</TableCell>
                                                    <TableCell className="max-w-md truncate text-sm">{log.message}</TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">
                                                        {format(new Date(log.created_at), 'MMM d, HH:mm')}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Staff Dialog */}
            <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}</DialogTitle>
                        <DialogDescription>
                            {editingStaff ? 'Update staff details' : 'Add a new team member'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input
                                value={staffForm.name}
                                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                                placeholder="Full name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Phone *</Label>
                            <Input
                                value={staffForm.phone}
                                onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                                placeholder="+233 XX XXX XXXX"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={staffForm.email}
                                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                                placeholder="email@example.com"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Role</Label>
                            <Select value={staffForm.role} onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="staff">Staff</SelectItem>
                                    <SelectItem value="manager">Manager</SelectItem>
                                    <SelectItem value="senior">Senior</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setShowStaffDialog(false)}>Cancel</Button>
                            <Button onClick={handleSaveStaff}>
                                {editingStaff ? 'Update' : 'Add'} Staff
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Service Dialog */}
            <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
                        <DialogDescription>
                            {editingService ? 'Update service details' : 'Add a new service to your menu'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Service Name *</Label>
                            <Input
                                value={serviceForm.name}
                                onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                                placeholder="e.g., Haircut, Manicure"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={serviceForm.description}
                                onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                                placeholder="Brief description..."
                                rows={2}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Price (GH₵) *</Label>
                                <Input
                                    type="number"
                                    value={serviceForm.price}
                                    onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                                    placeholder="50"
                                    min="0"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Duration</Label>
                                <Select 
                                    value={serviceForm.duration_minutes}
                                    onValueChange={(v) => setServiceForm({ ...serviceForm, duration_minutes: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="15">15 mins</SelectItem>
                                        <SelectItem value="30">30 mins</SelectItem>
                                        <SelectItem value="45">45 mins</SelectItem>
                                        <SelectItem value="60">1 hour</SelectItem>
                                        <SelectItem value="90">1.5 hours</SelectItem>
                                        <SelectItem value="120">2 hours</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setShowServiceDialog(false)}>Cancel</Button>
                            <Button onClick={handleSaveService}>
                                {editingService ? 'Update' : 'Add'} Service
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Message Dialog */}
            <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Send Message to Clients</DialogTitle>
                        <DialogDescription>Compose and send a message via email and/or SMS</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Recipients</Label>
                            <Select 
                                value={messageForm.recipient_type}
                                onValueChange={(v) => setMessageForm({ ...messageForm, recipient_type: v, client_id: '' })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Clients ({clients.length}) - Uses {clients.length} credits</SelectItem>
                                    <SelectItem value="specific">Specific Client - Uses 1 credit</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {messageForm.recipient_type === 'specific' && (
                            <div className="space-y-2">
                                <Label>Select Client</Label>
                                <Select 
                                    value={messageForm.client_id}
                                    onValueChange={(v) => setMessageForm({ ...messageForm, client_id: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Choose a client" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {clients.map((client) => (
                                            <SelectItem key={client.id} value={client.id}>
                                                {client.name} - {client.phone}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Subject (for email)</Label>
                            <Input
                                value={messageForm.subject}
                                onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                                placeholder="Message subject"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Message</Label>
                            <Textarea
                                value={messageForm.message}
                                onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                                placeholder="Your message..."
                                rows={5}
                            />
                            <p className="text-xs text-muted-foreground">
                                {messageForm.message.length} characters
                                {messageForm.send_sms && messageForm.message.length > 160 && (
                                    <span className="text-amber-500"> (SMS will be split into {Math.ceil(messageForm.message.length / 160)} messages)</span>
                                )}
                            </p>
                        </div>

                        <div className="flex items-center gap-6 p-4 rounded-lg bg-secondary/50">
                            <Label className="text-sm font-medium">Send via:</Label>
                            <div className="flex items-center gap-2">
                                <Switch 
                                    id="send_email"
                                    checked={messageForm.send_email}
                                    onCheckedChange={(v) => setMessageForm({ ...messageForm, send_email: v })}
                                />
                                <Label htmlFor="send_email" className="text-sm cursor-pointer">Email</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch 
                                    id="send_sms"
                                    checked={messageForm.send_sms}
                                    onCheckedChange={(v) => setMessageForm({ ...messageForm, send_sms: v })}
                                />
                                <Label htmlFor="send_sms" className="text-sm cursor-pointer">SMS</Label>
                            </div>
                        </div>
                        
                        <Alert>
                            <Zap className="h-4 w-4" />
                            <AlertDescription>
                                This will use <strong>{messageForm.recipient_type === 'all' ? clients.length : 1} credit{messageForm.recipient_type === 'all' && clients.length !== 1 ? 's' : ''}</strong>.
                                You have {notificationCredits} credits remaining.
                            </AlertDescription>
                        </Alert>

                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
                            <Button 
                                onClick={handleSendMessage} 
                                disabled={loading || (messageForm.recipient_type === 'all' ? clients.length > notificationCredits : notificationCredits < 1)}
                            >
                                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                Send Message
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Test Notification Dialog */}
            <Dialog open={showTestNotificationDialog} onOpenChange={setShowTestNotificationDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Send Test Notification</DialogTitle>
                        <DialogDescription>
                            Send a test notification to verify your setup is working. Uses 1 credit.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <Alert>
                            <Zap className="h-4 w-4" />
                            <AlertDescription>
                                This will use <strong>1 credit</strong>. You have {notificationCredits} credits remaining.
                            </AlertDescription>
                        </Alert>
                        
                        <div className="space-y-2">
                            <Label>Phone Number (for SMS)</Label>
                            <Input
                                value={testNotificationForm.phone}
                                onChange={(e) => setTestNotificationForm({ ...testNotificationForm, phone: e.target.value })}
                                placeholder="+233 XX XXX XXXX"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email Address</Label>
                            <Input
                                type="email"
                                value={testNotificationForm.email}
                                onChange={(e) => setTestNotificationForm({ ...testNotificationForm, email: e.target.value })}
                                placeholder="your@email.com"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Send</Label>
                            <Select 
                                value={testNotificationForm.type}
                                onValueChange={(v) => setTestNotificationForm({ ...testNotificationForm, type: v })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="both">Both SMS & Email (1 credit)</SelectItem>
                                    <SelectItem value="sms">SMS Only (1 credit)</SelectItem>
                                    <SelectItem value="email">Email Only (1 credit)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setShowTestNotificationDialog(false)}>Cancel</Button>
                            <Button onClick={handleTestNotification} disabled={testingNotification || notificationCredits < 1}>
                                {testingNotification ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <TestTube className="w-4 h-4 mr-2" />
                                )}
                                Send Test (1 credit)
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}